// CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
// Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
// SPDX-License-Identifier: LicenseRef-Cercle-Interne
//
// Fichier : apps/web/src/lib/navigation.ts
// Rôle : suit l'état d'une navigation, du clic à l'écran prêt.
// Référence : CIG-SPECIFICATION.md §5.5 ; designsystem.md.

import { defineStore } from 'pinia';

/**
 * État d'une navigation.
 *
 * Un écran ne doit jamais s'afficher à moitié rempli : la vue précédente reste
 * en place, la saisie est neutralisée, un rouet marque l'attente, et le
 * remplacement n'a lieu qu'une fois la nouvelle vue chargée. Faire autrement
 * produit un clignotement de squelettes que l'utilisateur lit comme une panne.
 *
 * Le décompte porte sur des tâches, non sur un drapeau : une vue peut lancer
 * plusieurs chargements, et l'écran n'est prêt que lorsque tous ont abouti.
 */
export const useNavigation = defineStore('navigation', {
  state: () => ({
    /** Destination visée, pour marquer le lien cliqué d'un rouet. */
    cible: null as string | null,
    /** Route en cours de résolution, composant compris. */
    routeEnAttente: false,
    /** Chargements déclarés par la vue qui vient d'être montée. */
    taches: 0,
    /**
     * Toute première navigation de la session. Elle ne remplace aucun écran :
     * l'attente s'y montre dans la vue, non par-dessus elle.
     */
    premiere: true,
  }),

  getters: {
    /**
     * Vrai tant que l'écran visé n'est pas prêt à être montré.
     *
     * Le premier chargement en est exclu : il n'y a pas d'écran à remplacer,
     * rien à préserver, et neutraliser la saisie y priverait l'utilisateur de
     * ses raccourcis pendant toute l'arrivée des données. La vue qui se charge
     * le dit elle-même, à sa place.
     */
    enCours: (s) => !s.premiere && (s.routeEnAttente || s.taches > 0),
  },

  actions: {
    /**
     * Ouvre une navigation. La toute première n'en est pas une : rien n'est
     * encore à l'écran, il n'y a donc ni transition à ménager ni saisie à
     * protéger.
     */
    demarrer(chemin: string, initiale: boolean): void {
      this.premiere = initiale;
      this.cible = initiale ? null : chemin;
      this.routeEnAttente = true;
    },

    /**
     * La route est résolue et son composant monté. Les chargements déclarés
     * pendant le montage tiennent désormais l'attente à eux seuls.
     */
    routeArrivee(): void {
      this.routeEnAttente = false;
      if (this.taches === 0) this.cible = null;
    },

    /**
     * Déclare un chargement de vue. L'écran reste en attente jusqu'à son terme,
     * qu'il réussisse ou échoue : une vue en erreur doit se montrer, pas rester
     * derrière un rouet perpétuel.
     */
    async tache<T>(travail: Promise<T>): Promise<T> {
      this.taches += 1;
      try {
        return await travail;
      } finally {
        this.taches -= 1;
        if (this.taches === 0 && !this.routeEnAttente) this.cible = null;
      }
    },
  },
});
